import React, { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import {
  Container,
  Box,
  Typography,
  Button,
  Grid,
  Card,
  CardContent,
  CardMedia,
  CardActionArea,
  Chip,
  IconButton,
  Dialog,
  DialogTitle,
  DialogContent,
  Tabs,
  Tab,
  Alert,
  CircularProgress,
  TextField,
  InputAdornment,
  Menu,
  MenuItem,
  Badge,
} from '@mui/material';
import {
  Add as AddIcon,
  Search as SearchIcon,
  FilterList as FilterIcon,
  Edit as EditIcon,
  Delete as DeleteIcon,
  MoreVert as MoreIcon,
  CheckCircle as CheckIcon,
  Public as PublicIcon,
  Lock as LockIcon,
} from '@mui/icons-material';
import toast from 'react-hot-toast';
import PatternUpload from '../../components/SPIF/PatternUpload';

const authHeaders = (extra = {}) => {
  const token = localStorage.getItem('token');
  return {
    ...extra,
    ...(token ? { Authorization: `Bearer ${token}` } : {}),
  };
};

const PatternManagementPage = () => {
  const { brandId } = useParams();
  const navigate = useNavigate();

  const [patterns, setPatterns] = useState([]);
  const [loading, setLoading] = useState(true);
  const [uploadDialogOpen, setUploadDialogOpen] = useState(false);
  const [activeTab, setActiveTab] = useState(0);
  const [searchQuery, setSearchQuery] = useState('');
  const [categoryFilter, setCategoryFilter] = useState('all');
  const [statusFilter, setStatusFilter] = useState('all');
  const [anchorEl, setAnchorEl] = useState(null);
  const [selectedPattern, setSelectedPattern] = useState(null);

  // Mock user - in production, get from auth context
  const userId = '507f1f77bcf86cd799439011';

  const categories = ['all', 'Tops', 'Bottoms', 'Dresses', 'Outerwear', 'Accessories'];
  const statusOptions = ['all', 'draft', 'published', 'archived'];

  useEffect(() => {
    fetchPatterns();
  }, [brandId, categoryFilter, statusFilter, searchQuery]);

  const fetchPatterns = async () => {
    try {
      setLoading(true);

      const params = new URLSearchParams();
      if (brandId) params.append('brandId', brandId);
      if (categoryFilter !== 'all') params.append('category', categoryFilter);
      if (statusFilter !== 'all') params.append('status', statusFilter);
      if (searchQuery) params.append('search', searchQuery);

      const response = await fetch(`/api/patterns?${params.toString()}`, {
        headers: authHeaders(),
      });

      if (!response.ok) {
        throw new Error('Failed to fetch patterns');
      }

      const data = await response.json();
      setPatterns(data.patterns || []);
    } catch (error) {
      console.error('Error fetching patterns:', error);
      toast.error('Failed to load patterns');
    } finally {
      setLoading(false);
    }
  };

  const handleUploadSuccess = (newPattern) => {
    setUploadDialogOpen(false);
    toast.success('Pattern created successfully!');
    fetchPatterns();
  };

  const handleMenuOpen = (event, pattern) => {
    setAnchorEl(event.currentTarget);
    setSelectedPattern(pattern);
  };

  const handleMenuClose = () => {
    setAnchorEl(null);
    setSelectedPattern(null);
  };

  const handleEditPattern = () => {
    // TODO: Implement pattern edit
    toast.info('Edit functionality coming soon');
    handleMenuClose();
  };

  const handleDeletePattern = async () => {
    if (!selectedPattern) return;

    if (!window.confirm(`Are you sure you want to archive "${selectedPattern.name}"?`)) {
      handleMenuClose();
      return;
    }

    try {
      const response = await fetch(`/api/patterns/${selectedPattern._id}`, {
        method: 'DELETE',
        headers: authHeaders(),
      });

      if (!response.ok) {
        throw new Error('Failed to delete pattern');
      }

      toast.success('Pattern archived successfully');
      fetchPatterns();
    } catch (error) {
      console.error('Error deleting pattern:', error);
      toast.error('Failed to archive pattern');
    } finally {
      handleMenuClose();
    }
  };

  const handlePublishPattern = async () => {
    if (!selectedPattern) return;

    try {
      const response = await fetch(`/api/patterns/${selectedPattern._id}`, {
        method: 'PATCH',
        headers: authHeaders({ 'Content-Type': 'application/json' }),
        body: JSON.stringify({
          status: 'published',
          available: true,
        }),
      });

      if (!response.ok) {
        throw new Error('Failed to publish pattern');
      }

      toast.success('Pattern published successfully');
      fetchPatterns();
    } catch (error) {
      console.error('Error publishing pattern:', error);
      toast.error('Failed to publish pattern');
    } finally {
      handleMenuClose();
    }
  };

  const filteredPatterns = patterns.filter((pattern) => {
    // Tab filter
    if (activeTab === 0) {
      // All patterns
      return true;
    } else if (activeTab === 1) {
      // Brand patterns only
      return pattern.brandId;
    } else if (activeTab === 2) {
      // Global patterns only
      return !pattern.brandId;
    }
    return true;
  });

  return (
    <Container maxWidth="xl" sx={{ py: 4 }}>
      {/* Header */}
      <Box sx={{ mb: 4 }}>
        <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 2 }}>
          <Typography variant="h4" fontWeight={700}>
            Pattern Library
          </Typography>
          <Button
            variant="contained"
            startIcon={<AddIcon />}
            onClick={() => setUploadDialogOpen(true)}
            size="large"
          >
            New Pattern
          </Button>
        </Box>
        <Typography variant="body1" color="text.secondary">
          Manage pattern blocks for your SPIF garment generation workflow
        </Typography>
      </Box>

      {/* Tabs */}
      <Box sx={{ mb: 3 }}>
        <Tabs value={activeTab} onChange={(e, v) => setActiveTab(v)}>
          <Tab label={`All Patterns (${patterns.length})`} />
          <Tab label={`Brand Patterns (${patterns.filter((p) => p.brandId).length})`} />
          <Tab label={`Global Patterns (${patterns.filter((p) => !p.brandId).length})`} />
        </Tabs>
      </Box>

      {/* Filters */}
      <Box sx={{ mb: 3, display: 'flex', gap: 2, flexWrap: 'wrap', alignItems: 'center' }}>
        <TextField
          placeholder="Search patterns..."
          value={searchQuery}
          onChange={(e) => setSearchQuery(e.target.value)}
          size="small"
          sx={{ minWidth: 300 }}
          InputProps={{
            startAdornment: (
              <InputAdornment position="start">
                <SearchIcon />
              </InputAdornment>
            ),
          }}
        />

        <Box sx={{ display: 'flex', gap: 1, flexWrap: 'wrap' }}>
          {categories.map((cat) => (
            <Chip
              key={cat}
              label={cat === 'all' ? 'All Categories' : cat}
              onClick={() => setCategoryFilter(cat)}
              color={categoryFilter === cat ? 'primary' : 'default'}
              variant={categoryFilter === cat ? 'filled' : 'outlined'}
            />
          ))}
        </Box>

        <Box sx={{ display: 'flex', gap: 1, flexWrap: 'wrap' }}>
          {statusOptions.map((status) => (
            <Chip
              key={status}
              label={status === 'all' ? 'All Status' : status.charAt(0).toUpperCase() + status.slice(1)}
              onClick={() => setStatusFilter(status)}
              color={statusFilter === status ? 'secondary' : 'default'}
              variant={statusFilter === status ? 'filled' : 'outlined'}
              size="small"
            />
          ))}
        </Box>
      </Box>

      {/* Loading State */}
      {loading && (
        <Box sx={{ display: 'flex', justifyContent: 'center', py: 8 }}>
          <CircularProgress />
        </Box>
      )}

      {/* Empty State */}
      {!loading && filteredPatterns.length === 0 && (
        <Alert severity="info" sx={{ mt: 4 }}>
          <Typography variant="body2">
            No patterns found. Create your first pattern to get started!
          </Typography>
        </Alert>
      )}

      {/* Pattern Grid */}
      {!loading && filteredPatterns.length > 0 && (
        <Grid container spacing={3}>
          {filteredPatterns.map((pattern) => (
            <Grid item xs={12} sm={6} md={4} lg={3} key={pattern._id}>
              <Card
                sx={{
                  height: '100%',
                  position: 'relative',
                  '&:hover': {
                    boxShadow: 6,
                  },
                }}
              >
                {/* More Menu */}
                <IconButton
                  sx={{ position: 'absolute', top: 8, right: 8, bgcolor: 'background.paper', zIndex: 1 }}
                  size="small"
                  onClick={(e) => handleMenuOpen(e, pattern)}
                >
                  <MoreIcon />
                </IconButton>

                <CardActionArea>
                  {/* Pattern Image */}
                  <Box sx={{ position: 'relative', width: '100%', paddingTop: '100%', bgcolor: 'grey.100' }}>
                    <CardMedia
                      component="img"
                      image={pattern.imageUrl || 'https://via.placeholder.com/400'}
                      alt={pattern.name}
                      sx={{
                        position: 'absolute',
                        top: 0,
                        left: 0,
                        width: '100%',
                        height: '100%',
                        objectFit: 'cover',
                      }}
                    />

                    {/* Badges */}
                    <Box sx={{ position: 'absolute', top: 12, left: 12, display: 'flex', gap: 1, flexDirection: 'column' }}>
                      <Chip
                        label={pattern.category}
                        size="small"
                        sx={{ bgcolor: 'white', fontWeight: 600 }}
                      />
                      {pattern.status === 'published' && pattern.available && (
                        <Chip
                          label="Published"
                          size="small"
                          color="success"
                          icon={<CheckIcon />}
                        />
                      )}
                      {pattern.status === 'draft' && (
                        <Chip label="Draft" size="small" color="warning" />
                      )}
                      {pattern.comingSoon && (
                        <Chip label="Coming Soon" size="small" color="info" />
                      )}
                    </Box>

                    {/* Visibility Badge */}
                    <Chip
                      icon={pattern.brandId ? <LockIcon /> : <PublicIcon />}
                      label={pattern.brandId ? 'Private' : 'Global'}
                      size="small"
                      sx={{
                        position: 'absolute',
                        bottom: 12,
                        right: 12,
                        bgcolor: 'rgba(0,0,0,0.7)',
                        color: 'white',
                      }}
                    />
                  </Box>

                  <CardContent>
                    <Typography variant="h6" fontWeight={700} gutterBottom>
                      {pattern.name}
                    </Typography>

                    <Typography
                      variant="body2"
                      color="text.secondary"
                      sx={{
                        mb: 2,
                        display: '-webkit-box',
                        WebkitLineClamp: 2,
                        WebkitBoxOrient: 'vertical',
                        overflow: 'hidden',
                      }}
                    >
                      {pattern.description}
                    </Typography>

                    {/* Stats */}
                    <Box sx={{ display: 'flex', gap: 1, flexWrap: 'wrap' }}>
                      <Chip label={`${pattern.pieceCount} pieces`} size="small" variant="outlined" />
                      {pattern.measurements && (
                        <Chip
                          label={`${pattern.measurements.length} measurements`}
                          size="small"
                          variant="outlined"
                        />
                      )}
                      {pattern.stats?.timesUsed > 0 && (
                        <Chip
                          label={`Used ${pattern.stats.timesUsed}×`}
                          size="small"
                          color="primary"
                          variant="outlined"
                        />
                      )}
                    </Box>

                    {/* Tags */}
                    {pattern.tags && pattern.tags.length > 0 && (
                      <Box sx={{ display: 'flex', gap: 0.5, flexWrap: 'wrap', mt: 1 }}>
                        {pattern.tags.slice(0, 3).map((tag, index) => (
                          <Chip key={index} label={tag} size="small" sx={{ fontSize: '0.7rem', height: 20 }} />
                        ))}
                        {pattern.tags.length > 3 && (
                          <Chip label={`+${pattern.tags.length - 3}`} size="small" sx={{ fontSize: '0.7rem', height: 20 }} />
                        )}
                      </Box>
                    )}
                  </CardContent>
                </CardActionArea>
              </Card>
            </Grid>
          ))}
        </Grid>
      )}

      {/* Context Menu */}
      <Menu anchorEl={anchorEl} open={Boolean(anchorEl)} onClose={handleMenuClose}>
        {selectedPattern?.status === 'draft' && (
          <MenuItem onClick={handlePublishPattern}>
            <CheckIcon sx={{ mr: 1 }} fontSize="small" />
            Publish Pattern
          </MenuItem>
        )}
        <MenuItem onClick={handleEditPattern}>
          <EditIcon sx={{ mr: 1 }} fontSize="small" />
          Edit Pattern
        </MenuItem>
        <MenuItem onClick={handleDeletePattern} sx={{ color: 'error.main' }}>
          <DeleteIcon sx={{ mr: 1 }} fontSize="small" />
          Archive Pattern
        </MenuItem>
      </Menu>

      {/* Upload Dialog */}
      <Dialog
        open={uploadDialogOpen}
        onClose={() => setUploadDialogOpen(false)}
        maxWidth="lg"
        fullWidth
      >
        <DialogTitle sx={{ fontWeight: 700, fontSize: '1.5rem' }}>
          Create New Pattern
        </DialogTitle>
        <DialogContent>
          <Box sx={{ pt: 2 }}>
            <PatternUpload
              brandId={brandId}
              userId={userId}
              onSuccess={handleUploadSuccess}
              onCancel={() => setUploadDialogOpen(false)}
            />
          </Box>
        </DialogContent>
      </Dialog>
    </Container>
  );
};

export default PatternManagementPage;
